import {
  Button,
  FormControl,
  FormLabel,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  SimpleGrid,
  FormHelperText,
  Text,
} from '@chakra-ui/react';
import PropTypes from 'prop-types';
import { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { DEFAULT_COUNTRY } from '../constants/countries';
import { normalizeBillingDetails, validateBillingEmail } from '../calc/billingDetails';
import BillingCountryField from './BillingCountryField';
import BillingStateField from './BillingStateField';

const BillingDetailsModal = ({ isOpen, billingDetails, onClose, onSave }) => {
  const { register, handleSubmit, reset, control, watch } = useForm();
  const country = watch('country');

  useEffect(() => {
    if (isOpen) {
      reset({
        country: DEFAULT_COUNTRY,
        ...billingDetails,
      });
    }
  }, [billingDetails, isOpen, reset]);

  const onSubmit = (values) => {
    onSave(normalizeBillingDetails(values, billingDetails));
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg" closeOnOverlayClick={false}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Billing details required</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Text fontSize="sm" color="secondaryGray.600" mb={4}>
            Complete invoice billing details for this customer before creating an invoice.
          </Text>
          <SimpleGrid columns={1} spacing={4}>
            <FormControl isRequired>
              <FormLabel>Company name</FormLabel>
              <Input {...register('invoiceCompanyName', { required: true })} />
            </FormControl>
            <FormControl isRequired>
              <FormLabel>Person&apos;s name</FormLabel>
              <Input {...register('billingContactName', { required: true })} />
            </FormControl>
            <FormControl>
              <FormLabel>Person&apos;s title</FormLabel>
              <Input {...register('billingContactTitle')} placeholder="e.g. CFO" />
            </FormControl>
            <FormControl isRequired>
              <FormLabel>Billing email(s)</FormLabel>
              <Input
                placeholder="billing@example.com, ap@example.com"
                {...register('billingEmail', {
                  required: true,
                  validate: validateBillingEmail,
                })}
              />
              <FormHelperText>Separate multiple emails with commas.</FormHelperText>
            </FormControl>
            <FormControl isRequired>
              <FormLabel>Address line 1</FormLabel>
              <Input {...register('addressLine1', { required: true })} />
            </FormControl>
            <FormControl>
              <FormLabel>Address line 2</FormLabel>
              <Input {...register('addressLine2')} />
            </FormControl>
            <FormControl isRequired>
              <FormLabel>City</FormLabel>
              <Input {...register('city', { required: true })} />
            </FormControl>
            <FormControl isRequired>
              <FormLabel>Country</FormLabel>
              <Controller
                control={control}
                name="country"
                defaultValue={DEFAULT_COUNTRY}
                rules={{ required: true }}
                render={({ field }) => <BillingCountryField {...field} />}
              />
            </FormControl>
            <FormControl isRequired>
              <FormLabel>State / region</FormLabel>
              <Controller
                control={control}
                name="state"
                rules={{
                  validate: (value) =>
                    String(value || '').trim().length > 0 || 'State / region is required',
                }}
                render={({ field }) => (
                  <BillingStateField {...field} country={country} />
                )}
              />
            </FormControl>
            <FormControl isRequired>
              <FormLabel>Postal code</FormLabel>
              <Input {...register('postalCode', { required: true })} />
            </FormControl>
          </SimpleGrid>
        </ModalBody>
        <ModalFooter gap={3}>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="brand" onClick={handleSubmit(onSubmit)}>
            Save billing details
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

BillingDetailsModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  billingDetails: PropTypes.object,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
};

export default BillingDetailsModal;
